import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HelpCircle, 
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  FileText,
  Clock,
  Truck,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';

const faqItems = [
  {
    id: 'inspection',
    icon: ClipboardCheck,
    question: 'Comment effectuer une inspection pré-départ?',
    answer: 'Accédez à la section "Inspection" depuis le tableau de bord. Entrez le kilométrage, utilisez le bouton "TOUT CONFORME" pour marquer tous les éléments OK, ou vérifiez chaque élément individuellement. N\'oubliez pas de signer avant d\'enregistrer. L\'inspection est valide pour 24 heures.'
  },
  {
    id: 'hours',
    icon: Clock,
    question: 'Comment fonctionnent les heures de service?',
    answer: 'Regles generales de conduite:\n\n• Cycle 1 (7 jours): Maximum 70 heures de travail\n• Cycle 2 (14 jours): Maximum 120 heures de travail\n• Maximum 13 heures de conduite par jour\n• Maximum 14 heures de service par jour\n• Minimum 10 heures de repos par jour\n\nPour remettre vos heures à zéro: 36h de repos consécutives (Cycle 1) ou 72h (Cycle 2).'
  },
  {
    id: 'report',
    icon: FileText,
    question: 'Comment créer un rapport journalier?',
    answer: 'Depuis le tableau de bord, cliquez sur "RAPPORT JOURNALIER". Entrez les informations de l\'entreprise, les heures de début/fin, et ajoutez vos arrêts (livraisons, cueillettes, pauses). Vous pouvez scanner des documents pour chaque arrêt. Une fois terminé, marquez le rapport comme "Terminé" pour pouvoir le partager.'
  },
  {
    id: 'vehicle',
    icon: Truck,
    question: 'Comment configurer mon véhicule?',
    answer: 'Vous pouvez indiquer votre vehicule directement depuis l ecran d accueil. Entrez les informations de votre tracteur (numéro d\'unité, plaque, marque, modèle) et ajoutez vos remorques. Pour un B-Train, ajoutez 2 remorques. Ces informations apparaîtront automatiquement dans vos rapports d\'inspection.'
  }
];

export const HelpPage = () => {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="page-wrapper" data-testid="help-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="text-slate-400" size={24} />
          </button>
          <div className="flex items-center gap-3">
            <HelpCircle className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Aide</h1>
              <p className="text-slate-400 text-sm">Support et FAQ</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* FAQ Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h2 className="text-lg font-bold text-white">Questions fréquentes</h2>
          </div>
          
          {faqItems.map((item) => {
            const Icon = item.icon;
            const isExpanded = expandedId === item.id;
            
            return (
              <div key={item.id} className="border-b border-slate-800 last:border-b-0">
                <button
                  onClick={() => toggleExpand(item.id)}
                  className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-800/50"
                  data-testid={`faq-${item.id}`}
                >
                  <Icon className="text-amber-500 flex-shrink-0" size={20} />
                  <span className="flex-1 text-white font-medium">{item.question}</span>
                  {isExpanded ? (
                    <ChevronUp className="text-slate-400" size={20} />
                  ) : (
                    <ChevronDown className="text-slate-400" size={20} />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="px-4 pb-4 pl-12">
                    <p className="text-slate-400 text-sm whitespace-pre-line">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h2 className="text-lg font-bold text-white mb-4">Besoin d'aide?</h2>
          
          <div className="space-y-3">
            <a
              href="mailto:support@truckdriverpro.ca"
              className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800"
            >
              <Mail className="text-blue-400" size={20} />
              <div>
                <p className="text-white font-medium">Email</p>
                <p className="text-slate-400 text-sm">support@truckdriverpro.ca</p>
              </div>
            </a>
            
            <a
              href="tel:+15145551234"
              className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800"
            >
              <Phone className="text-green-400" size={20} />
              <div>
                <p className="text-white font-medium">Téléphone</p>
                <p className="text-slate-400 text-sm">+1 (514) 555-1234</p>
              </div>
            </a>
          </div>
        </div>

        {/* App Version */}
        <div className="text-center py-4">
          <p className="text-slate-500 text-sm">TruckDriverPro v1.2.0</p>
          <p className="text-slate-600 text-xs mt-1">© 2026 Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
